/**
 * The four property facts a designer asks first: what shape of building, what
 * kind of dwelling, what the main walls are made of, and whether they are
 * insulated. Recorded on the survey (migration 0031), edited on the portal's
 * Survey page, validated by `PATCH /api/surveys/:id/property`.
 *
 * ABSENT IS NOT A VALUE. Every one of these is nullable on the survey and
 * `null` means "not recorded" — nobody has said. There is deliberately no
 * default and no "unknown" member: a screen that pre-selected "semi-detached"
 * or "cavity" would be the platform guessing on the surveyor's behalf, and a
 * guess that looks like an answer is the thing this product exists to expose.
 *
 * WHERE THE KEYS COME FROM — reuse first, invention last:
 *
 *  - `WallConstruction` and `WallInsulation` mirror the canonical vocabulary
 *    `@sts/epc` already translates certificate prose into
 *    (`packages/epc/src/vocabulary.ts`), member for member, minus that
 *    package's `"unknown"` (which is "the EPC sentence did not say"; here that
 *    is `null`). `SurveyedFabric.walls` in `@sts/epc/survey-types.ts` is typed
 *    with those same unions, so a survey answer can be compared against a
 *    certificate without a mapping table. The API asserts the alignment at
 *    compile time (`apps/api/src/survey/survey-writer.ts`) because the API is
 *    the one workspace that depends on both packages; this package takes no
 *    dependency on `@sts/epc`, for the same reason `property.ts` takes none on
 *    `@sts/bus`.
 *
 *    DIVERGENCES, both deliberate:
 *      * `WallConstruction` adds `"other"` — a surveyor who looked and found
 *        something outside the list has an answer; the EPC reader never
 *        produces one because it only ever reads RdSAP's own phrases.
 *      * `WallInsulation` offers three of the EPC's five known insulation
 *        states (`none`, `partial`, `insulated`, `internal`, `external`).
 *        The EPC distinguishes `internal` and `external` insulation; the
 *        question asked here is only "is it insulated", so both of those are
 *        `"insulated"` from the surveyor's side. A reconciliation that later
 *        compares the two must treat EPC `internal`/`external` as agreeing with
 *        survey `insulated`.
 *
 *  - `BuiltForm` and `PropertyType` have no enum anywhere in the codebase yet:
 *    `EpcCertificate.builtForm`/`.propertyType` are raw strings (the register
 *    sends integer codes) and TrustMark's `propertyDetachment`/`propertyType`
 *    are free RIAPI taxonomy strings. So the keys are the kebab-case forms of
 *    RdSAP's own labels — six built forms ("Detached", "Semi-Detached",
 *    "End-Terrace", "Mid-Terrace", "Enclosed End-Terrace", "Enclosed
 *    Mid-Terrace") and five property types ("House", "Bungalow", "Flat",
 *    "Maisonette", "Park home") — which is the vocabulary both the EPC register
 *    and a retrofit assessor already speak.
 *
 * The labels here are the plain English a screen prints. They live beside the
 * keys so the portal's selects and any document template read one list.
 */

export type BuiltForm =
  | "detached"
  | "semi-detached"
  | "mid-terrace"
  | "end-terrace"
  | "enclosed-mid-terrace"
  | "enclosed-end-terrace";

export type PropertyType = "house" | "bungalow" | "flat" | "maisonette" | "park-home";

/** Mirrors `WallConstruction` in `@sts/epc`, minus `"unknown"`, plus `"other"`. */
export type WallConstruction =
  | "cavity"
  | "solid-brick"
  | "solid-stone"
  | "timber-frame"
  | "system-built"
  | "cob"
  | "park-home"
  | "other";

/** Mirrors `WallInsulation` in `@sts/epc`: `none`, `partial`, `insulated`. */
export type WallInsulation = "insulated" | "partial" | "none";

/**
 * Key and label, in the order a select should offer them. Ordered by how
 * common each is in UK housing stock rather than alphabetically, so the likely
 * answer is near the top.
 */
export interface VocabularyOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

export const BUILT_FORM_OPTIONS: readonly VocabularyOption<BuiltForm>[] = [
  { value: "detached", label: "Detached" },
  { value: "semi-detached", label: "Semi-detached" },
  { value: "mid-terrace", label: "Mid-terrace" },
  { value: "end-terrace", label: "End-terrace" },
  { value: "enclosed-mid-terrace", label: "Enclosed mid-terrace" },
  { value: "enclosed-end-terrace", label: "Enclosed end-terrace" },
];

export const PROPERTY_TYPE_OPTIONS: readonly VocabularyOption<PropertyType>[] = [
  { value: "house", label: "House" },
  { value: "bungalow", label: "Bungalow" },
  { value: "flat", label: "Flat" },
  { value: "maisonette", label: "Maisonette" },
  { value: "park-home", label: "Park home" },
];

export const WALL_CONSTRUCTION_OPTIONS: readonly VocabularyOption<WallConstruction>[] = [
  { value: "cavity", label: "Cavity" },
  { value: "solid-brick", label: "Solid brick" },
  { value: "solid-stone", label: "Solid stone" },
  { value: "timber-frame", label: "Timber frame" },
  { value: "system-built", label: "System built" },
  { value: "cob", label: "Cob" },
  { value: "park-home", label: "Park home" },
  { value: "other", label: "Other" },
];

export const WALL_INSULATION_OPTIONS: readonly VocabularyOption<WallInsulation>[] = [
  { value: "insulated", label: "Insulated" },
  { value: "partial", label: "Partially insulated" },
  { value: "none", label: "Not insulated" },
];

/**
 * The runtime arrays the API validates against, derived from the option lists
 * so there is one list per vocabulary rather than two to keep in step. The
 * CHECK constraints in migration 0031 restate them; where the two disagree the
 * database wins and this file is wrong.
 */
export const BUILT_FORMS: readonly BuiltForm[] = BUILT_FORM_OPTIONS.map((o) => o.value);
export const PROPERTY_TYPES: readonly PropertyType[] = PROPERTY_TYPE_OPTIONS.map(
  (o) => o.value,
);
export const WALL_CONSTRUCTIONS: readonly WallConstruction[] =
  WALL_CONSTRUCTION_OPTIONS.map((o) => o.value);
export const WALL_INSULATIONS: readonly WallInsulation[] = WALL_INSULATION_OPTIONS.map(
  (o) => o.value,
);

/** The four facts as the API carries them. `null` is "not recorded". */
export interface SurveyPropertyFacts {
  readonly builtForm: BuiltForm | null;
  readonly propertyType: PropertyType | null;
  readonly wallConstruction: WallConstruction | null;
  readonly wallInsulation: WallInsulation | null;
}

/** The field names of `SurveyPropertyFacts`, for iterating and for refusing strangers. */
export const SURVEY_PROPERTY_FIELDS = [
  "builtForm",
  "propertyType",
  "wallConstruction",
  "wallInsulation",
] as const satisfies readonly (keyof SurveyPropertyFacts)[];
